import { toast } from "react-toastify";
import axios from "axios";

export const API_BASE_URL = (process.env.REACT_APP_API_URL || "").trim();
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // Fail fast on very slow connections so we can surface a clear message.
  timeout: 20000,
});
// (extractMessage & pickMessage are declared earlier in this file)

const NETWORK_TOAST_ID = "network-connectivity-error";
let isRedirectingToLogin = false;

const redirectToLogin = (): void => {
  try {
    clearTokens();
  } catch {
    // ignore storage errors
  }

  if (typeof window !== "undefined" && window.location?.pathname === "/login") {
    return;
  }

  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  if (typeof window !== "undefined") {
    window.location.replace("/login");
  }
};

const isNetworkOrTimeoutError = (error: any): boolean => {
  if (!error) return false;
  if (!error.response) return true; // offline / DNS / blocked
  if (error.code && String(error.code).toLowerCase() === "econnaborted")
    return true; // axios timeout
  if (typeof error.message === "string" && error.message.includes("timeout"))
    return true;
  if (typeof error.message === "string" && error.message.includes("Network Error"))
    return true;
  return false;
};

const showErrorToast = (message: string, toastId?: string) => {
  toast.error(String(message), {
    position: "top-right",
    toastId: toastId ? String(toastId) : String(message),
  });
};

const buildNetworkMessage = () => {
  const offline =
    typeof navigator !== "undefined" && navigator && navigator.onLine === false;
  if (offline) return "You appear to be offline. Please check your connection.";
  return "Connection issue detected. Please check your internet and try again.";
};

/**
 * A superadmin can flip a feature off while someone is mid-session, so a screen
 * that was allowed a minute ago can start refusing. The backend marks those
 * with `code: "FEATURE_DISABLED"`; we broadcast them instead of toasting a
 * generic error, and FeaturesProvider turns that into the upsell dialog.
 */
export const FEATURE_DISABLED_EVENT = "feature-disabled";

const isFeatureDisabledError = (error: any): string | null => {
  const data = error?.response?.data;
  if (Number(error?.response?.status) !== 403) return null;
  if (data?.code !== "FEATURE_DISABLED") return null;
  return typeof data?.feature === "string" ? data.feature : "";
};

const handleApiError = (error: any) => {
  try {
    if (isNetworkOrTimeoutError(error)) {
      showErrorToast(buildNetworkMessage(), NETWORK_TOAST_ID);
      return Promise.reject(error);
    }

    const disabledFeature = isFeatureDisabledError(error);
    if (disabledFeature !== null) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(FEATURE_DISABLED_EVENT, { detail: disabledFeature })
        );
      }
      return Promise.reject(error);
    }

    if (error?.config?.suppressToast) {
      return Promise.reject(error);
    }

    const status = Number(error?.response?.status);
    const apiError = error?.response?.data;
    const extracted =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || undefined;

    // Avoid surfacing raw "Request failed with status code 400/500" to end users.
    const message =
      Number.isFinite(status) && status >= 500
        ? "Server error. Please try again in a moment."
        : extracted || "Something went wrong. Please check your input and try again.";

    if (error?.response?.status !== 401) {
      showErrorToast(String(message), message);
    }
  } catch (err) {
    // ignore extraction errors
  }
  return Promise.reject(error);
};
/*
apiClient.interceptors.request.use(
  (config: any) => {
    try {
      const token = localStorage.getItem("api_token");
      if (token) {
        if (!config.headers) config.headers = {} as any;
        config.headers.Authorization = `${token}`;
      }

      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers) {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    } catch (e) {}
    return config;
  },
  (error) => Promise.reject(error)
);*/
apiClient.interceptors.request.use(
  (config: any) => {
    // api_token already holds KEYCLOAK token
    const token = localStorage.getItem("api_token");

    // user object already exists
    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : null;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Required for OrgContextGuard
    if (user?.organizationId) {
      config.headers["x-organization-id"] = user.organizationId;
    }

    // FormData safety
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(p => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
};
interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

type RetryableRequestConfig = {
  headers?: Record<string, any>;
  _retry?: boolean;
};
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as RetryableRequestConfig | undefined;

    if (
      !originalRequest ||
      error?.response?.status !== 401 ||
      originalRequest._retry
    ) {
      return handleApiError(error);
    }

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${token}`,
            };
            resolve(apiClient(originalRequest as any));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post<RefreshResponse>(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken }
      );

      const { accessToken, refreshToken: newRefresh } = res.data;

      localStorage.setItem("api_token", accessToken);
      localStorage.setItem("refresh_token", newRefresh);

      processQueue(null, accessToken);

      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      };

      return apiClient(originalRequest as any);
    } catch (refreshError) {
      processQueue(refreshError, null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);



export const attachApiErrorHandler = (instance: any) => {
  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => handleApiError(error)
  );
};
export const getAuthToken = (): string | null => {
  return localStorage.getItem("api_token");
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem("refresh_token");
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem("api_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);
};

export const clearTokens = (): void => {
  localStorage.removeItem("api_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_id");
  localStorage.removeItem("user");
  localStorage.removeItem(ORG_FEATURES_KEY);
};

export const getUserId = (): string | null => {
  return localStorage.getItem("user_id");
};

export const setUserId = (userId: string): void => {
  localStorage.setItem("user_id", userId);
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

const generateRandomColor = (): string => {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgb(${r}, ${g}, ${b})`;
};

const extractMessage = (input: any): string | undefined => {
  if (input === undefined || input === null) return undefined;
  if (typeof input === "string") return input;
  if (typeof input === "boolean") return undefined;
  if (Array.isArray(input)) {
    const flattened = input
      .map((item) => extractMessage(item))
      .filter((item): item is string => Boolean(item));
    return flattened.length ? flattened.join(", ") : undefined;
  }
  if (typeof input === "object") {
    const values = Object.values(input);
    for (const value of values) {
      const message = extractMessage(value);
      if (message) return message;
    }
    try {
      return JSON.stringify(input);
    } catch {
      return String(input);
    }
  }
  return String(input);
};

const pickMessage = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    const resolved = extractMessage(candidate);
    if (resolved) return resolved;
  }
  return undefined;
};

export const logout = async () => {
  const refreshToken = localStorage.getItem("refresh_token");

  try {
    try {
      const { disableCurrentBrowserWebPush } = await import("./backofficeWebPush");
      await disableCurrentBrowserWebPush();
    } catch (error) {
      console.warn("Failed to disable web push notifications during logout", error);
    }

    if (refreshToken) {
      await apiClient.post("/auth/logout", { refreshToken });
    }
  } finally {
    redirectToLogin();
  }
};


/** Feature switches for the signed-in organization, as returned by login/me. */
export type FeatureMap = Record<string, boolean>;

export const ORG_FEATURES_KEY = "org_features";

/** Fired whenever the cached feature map is replaced, so providers can re-read it. */
export const FEATURES_UPDATED_EVENT = "org-features-updated";

export const setOrgFeatures = (features: FeatureMap | undefined | null): void => {
  if (!features) return;
  localStorage.setItem(ORG_FEATURES_KEY, JSON.stringify(features));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(FEATURES_UPDATED_EVENT));
  }
};

/**
 * Reads the cached switch. An absent key means enabled — an organization with no
 * feature rows is all-enabled, and it keeps calls working before the map loads.
 * Use it to skip requests the backend would only answer with a 403.
 */
export const isFeatureEnabled = (feature: string): boolean => {
  const value = getOrgFeatures()[feature];
  return value === undefined ? true : !!value;
};

export const getOrgFeatures = (): FeatureMap => {
  try {
    const raw = localStorage.getItem(ORG_FEATURES_KEY);
    return raw ? (JSON.parse(raw) as FeatureMap) : {};
  } catch {
    return {};
  }
};

interface LoginAdminUser {
  organizationUserId: string;
  organizationId: string;
  roleId: string;
  role: string;
  permissions: string[];
}

interface LoginAdminResponse {
  /** NEW — branch on this. */
  mode?: "ORG_LOGIN" | "SUPERADMIN_OTP_REQUIRED";
  accessToken?: string;
  refreshToken?: string;
  features?: FeatureMap;
  user?: LoginAdminUser;
  /** Only on the OTP branch. */
  message?: string;
  channel?: "WHATSAPP" | "SMS" | string;
  expiresInMinutes?: number;
}

export type LoginResult =
  | { success: true; mode: "ORG_LOGIN"; user: LoginAdminUser; features: FeatureMap }
  | {
      success: true;
      mode: "SUPERADMIN_OTP_REQUIRED";
      message: string;
      channel: string;
      expiresInMinutes: number;
    }
  | { success: false; error: string };

/**
 * One field, two outcomes. `identifier` is either an email or a superadmin's
 * phone number, and it always goes over the wire as `email` — the backend
 * decides which branch to take. Omit the password for a phone number; the
 * backend does not require one in that case.
 */
export const login = async (
  identifier: string,
  password?: string
): Promise<LoginResult> => {
  try {
    const body: { email: string; password?: string } = { email: identifier };
    if (password) body.password = password;

    const response = await apiClient.post<LoginAdminResponse>(
      "auth/loginAdmin",
      body
    );

    const data = response.data;

    if (data?.mode === "SUPERADMIN_OTP_REQUIRED") {
      return {
        success: true,
        mode: "SUPERADMIN_OTP_REQUIRED",
        message: data.message || "Verification code sent",
        channel: data.channel || "WHATSAPP",
        expiresInMinutes: data.expiresInMinutes ?? 10,
      };
    }

    const { accessToken, refreshToken, user, features } = data;

    setTokens(accessToken as string, refreshToken as string);
    if (user?.organizationUserId) {
      setUserId(user.organizationUserId);
    }

    localStorage.setItem("user", JSON.stringify(user));
    setOrgFeatures(features);

    return {
      success: true,
      mode: "ORG_LOGIN",
      user: user as LoginAdminUser,
      features: features || {},
    };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.message,
        apiError?.error
      ) || "Login failed";

    return { success: false, error: errorMessage };
  }
};

interface RegisterAdminResponse {
  message: string;
  organization?: string;
  accountName?: string;
}
export const signup = async (payload: {
  fullName: string;
  email: string;
  password: string;
  organizationName: string;
  country: string;
  phone: string;
}) => {
  try {
    console.log('hii ')
    console.log('SIGNUP PAYLOAD RAW:', payload);
console.log('SIGNUP POST BODY:', {
  full_name: payload.fullName,
  email: payload.email,
  password: payload.password,
  organization_name: payload.organizationName,
  country: payload.country,
  phone: payload.phone,
});
    const response = await apiClient.post<RegisterAdminResponse>("auth/registerAdmin", {
      full_name: payload.fullName,
      email: payload.email,
      password: payload.password,
      organization_name: payload.organizationName,
      country: payload.country,
      phone: payload.phone,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.message,
        apiError?.error,
        apiError?.errors
      ) || "Registration failed";

    return { success: false, error: errorMessage };
  }
};

export const requestPasswordResetCode = async (email: string) => {
  try {
    const response = await apiClient.post("auth/forgot-password", { email });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Verification code sent";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to send verification code";
    return { success: false, error: errorMessage };
  }
};

export const verifyPasswordResetCode = async (email: string, code: string) => {
  try {
    const response = await apiClient.post("auth/verify-reset-code", {
      email,
      code,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Code verified";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Invalid verification code";
    return { success: false, error: errorMessage };
  }
};

export const resetPasswordWithCode = async (
  email: string,
  code: string,
  password: string,
  passwordConfirmation: string
) => {
  try {
    const response = await apiClient.post("auth/reset-password", {
      email,
      code,
      password,
      newPassword: passwordConfirmation,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Password reset successfully";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to reset password";
    return { success: false, error: errorMessage };
  }
};

export const changePassword = async (
  currentPassword: string,
  password: string,
  passwordConfirmation: string
) => {
  try {
    const response = await apiClient.post("auth/users/change-password", {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Password updated successfully";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to change password";
    return { success: false, error: errorMessage };
  }
};
